export type RangeMode = 'today' | '7d' | '30d' | '90d' | 'custom';

const RANGE_OPTIONS: { value: RangeMode; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: 'custom', label: 'Custom range' },
];

export const AnalyticsControls = ({
  rangeMode,
  onRangeModeChange,
  customStart,
  customEnd,
  onCustomStartChange,
  onCustomEndChange,
  excludeAdminActivity,
  onExcludeAdminActivityChange,
}: {
  rangeMode: RangeMode;
  onRangeModeChange: (mode: RangeMode) => void;
  customStart: string;
  customEnd: string;
  onCustomStartChange: (value: string) => void;
  onCustomEndChange: (value: string) => void;
  excludeAdminActivity: boolean;
  onExcludeAdminActivityChange: (value: boolean) => void;
}) => {
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="flex flex-col items-end gap-3">
      <div className="relative">
        <select
          value={rangeMode}
          onChange={(e) => onRangeModeChange(e.target.value as RangeMode)}
          className="px-4 py-2 pr-10 text-gray-900 bg-white border-2 border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none cursor-pointer"
        >
          {RANGE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value} className="text-gray-900 bg-white">
              {opt.label}
            </option>
          ))}
        </select>
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {rangeMode === 'custom' && (
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <input
            type="date"
            value={customStart}
            max={customEnd || today}
            onChange={(e) => onCustomStartChange(e.target.value)}
            className="px-3 py-2 text-sm text-gray-900 bg-white border-2 border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <span className="text-gray-500 text-sm">to</span>
          <input
            type="date"
            value={customEnd}
            min={customStart}
            max={today}
            onChange={(e) => onCustomEndChange(e.target.value)}
            className="px-3 py-2 text-sm text-gray-900 bg-white border-2 border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      )}

      <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={excludeAdminActivity}
          onChange={(e) => onExcludeAdminActivityChange(e.target.checked)}
          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        <span>Exclude Super Admin activity</span>
        <span
          title="Excludes users with the Super Admin role from the Total Users count, Users table, Active Users chart, Conversation & Message Analytics, Contact Reasons, and Feedback Analytics. Expert Performance is unaffected, since it measures experts resolving escalations rather than end-user activity."
          className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-200 text-gray-600 text-[10px] font-bold cursor-help"
        >
          i
        </span>
      </label>
    </div>
  );
};
